import api from "@/api/client";
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  TenantSwitchResponse,
  TwoFactorResendRequest,
  TwoFactorVerifyRequest,
  User,
} from "@/api/types";
import { Config } from "@/lib/config";

const TENANT_LOGIN_URL = Config.tenant_login_url;
const TENANT_REFRESH_URL = Config.tenant_refresh_url;
const TENANT_VERIFY_URL = Config.tenant_login_url.replace("tenant-login/", "tenant-verify-code/");
const TENANT_RESEND_URL = Config.tenant_login_url.replace("tenant-login/", "tenant-resend-verification-code/");
const TENANT_SWITCH_URL = Config.tenant_login_url.replace("tenant-login/", "tenant-switch/");

/**
 * Get a user by ID
 * @param id - The user ID to fetch
 * @param tenantDb - Tenant database parameter (required)
 * @returns Promise<User> - Single user object
 */
export const getUserById = async (id: string | number, tenantDb: string): Promise<User> => {
  const params = { db: tenantDb };
  const response = await api.get<User>(`/admin-users/${id}/`, { params });
  return response.data;
};

/**
 * Login a user with credentials
 * @param credentials - Login credentials (email, password, tenant)
 * @returns Promise<LoginResponse> - Login response with access and refresh tokens
 */
export const loginUser = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await api.post(TENANT_LOGIN_URL, credentials);
  return response.data;
};

/**
 * Refresh an access token using a refresh token
 * @param refreshData - Refresh token data
 * @returns Promise<RefreshTokenResponse> - New access token
 */
export const refreshToken = async (refreshData: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
  const response = await api.post(TENANT_REFRESH_URL, refreshData);
  return response.data;
};

/**
 * Verify a two-factor authentication code
 * @param verifyData - Verification data (email, tenant, code)
 * @returns Promise<LoginResponse> - Login response with tokens on success
 */
export const verifyTwoFactorCode = async (verifyData: TwoFactorVerifyRequest): Promise<LoginResponse> => {
  const response = await api.post(TENANT_VERIFY_URL, verifyData);
  return response.data;
};

/**
 * Resend the two-factor authentication code
 * @param resendData - Resend data (email, tenant)
 * @returns Promise<{ message: string }> - Success message
 */
export const resendTwoFactorCode = async (resendData: TwoFactorResendRequest): Promise<{ message: string }> => {
  const response = await api.post(TENANT_RESEND_URL, resendData);
  return response.data;
};

/**
 * Switch tenant by requesting a fresh JWT from the backend
 * @param targetTenant - The tenant ID to switch to
 * @returns Promise<TenantSwitchResponse> - New tokens scoped to the target tenant
 */
export const switchTenantApi = async (targetTenant: string): Promise<TenantSwitchResponse> => {
  const response = await api.post<TenantSwitchResponse>(TENANT_SWITCH_URL, {
    target_tenant: targetTenant,
  });
  return response.data;
};
